import React, { useState } from 'react';
import { areaMode, areaViewKey, applyModeIn, toggleKeyIn, dependentsIn, matchesQuery } from './permissionModel';

function SensitiveTag({ note, permKey }) {
  const [open, setOpen] = useState(false);
  const popId = `re-info-${permKey}`;
  return (
    <span className="re-sensitive">
      <span className="re-sensitive-tag">Sensitive</span>
      <button
        type="button"
        className="re-info"
        aria-label="Why this is sensitive"
        aria-expanded={open}
        aria-describedby={open ? popId : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        i
      </button>
      {open && <span id={popId} className="re-info-pop" role="note">{note}</span>}
    </span>
  );
}

export default function PermissionAreaCard({ area, selected, onChange, readOnly, query, allPermissions }) {
  const mode = areaMode(area, selected);
  const hasView = !!areaViewKey(area);
  const rows = area.permissions.filter((p) => matchesQuery(p, query));
  if (query && rows.length === 0) return null;
  const options = [['none', 'No access'], ...(hasView ? [['view', 'View']] : []), ['custom', 'Custom'], ['full', 'Full']];
  const activeMode = readOnly ? 'full' : mode;
  const isEnabled = ([value]) => !readOnly && value !== 'custom';

  const focusOption = (value) => {
    const el = document.getElementById(`re-seg-${area.key}-${value}`);
    if (el) el.focus();
  };

  const moveTo = (fromValue, direction) => {
    const enabled = options.filter(isEnabled);
    if (enabled.length === 0) return;
    const idx = enabled.findIndex(([value]) => value === fromValue);
    const base = idx === -1 ? 0 : idx;
    const nextIdx = (base + direction + enabled.length) % enabled.length;
    const [nextValue] = enabled[nextIdx];
    onChange(applyModeIn(area, nextValue, selected, allPermissions));
    focusOption(nextValue);
  };

  const onKeyDown = (value) => (e) => {
    if (readOnly) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      moveTo(value, 1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      moveTo(value, -1);
    }
  };

  return (
    <section className="re-card" id={`re-area-${area.key}`} aria-label={area.label}>
      <header className="re-card-head">
        <div>
          <h3 className="re-card-title">{area.label}</h3>
          <p className="re-card-summary">{area.summary}</p>
        </div>
        <div className="re-seg" role="radiogroup" aria-label={`${area.label} access`}>
          {options.map(([value, label]) => {
            const enabled = isEnabled([value]);
            const checked = activeMode === value;
            return (
              <button
                key={value}
                id={`re-seg-${area.key}-${value}`}
                type="button"
                role="radio"
                aria-checked={checked}
                tabIndex={checked ? 0 : -1}
                className={`re-seg-btn${checked ? ' is-on' : ''}`}
                disabled={readOnly || value === 'custom'}
                onClick={() => enabled && onChange(applyModeIn(area, value, selected, allPermissions))}
                onKeyDown={onKeyDown(value)}
              >
                {label}
              </button>
            );
          })}
        </div>
      </header>
      <ul className="re-rows">
        {rows.map((p) => {
          const deps = dependentsIn(allPermissions, p.key, selected);
          return (
            <li key={p.key} className="re-row">
              <label className="re-row-main">
                <input
                  type="checkbox"
                  aria-label={p.label}
                  checked={readOnly || selected.has(p.key)}
                  disabled={readOnly || deps > 0}
                  onChange={() => onChange(toggleKeyIn(area, p.key, selected, allPermissions))}
                />
                <span className="re-row-label">{p.label}</span>
                {p.sensitive && <SensitiveTag note={p.sensitiveNote} permKey={p.key} />}
              </label>
              <p className="re-row-help">{p.description}</p>
              {!readOnly && deps > 0 && <p className="re-row-req">{`Required by ${deps} selected permission(s).`}</p>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
